import './TikTokShopAuth.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { CopyButton } from '@Components/CopyButton';
import { Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page';
import { useSetError } from '@src/redux/modals';
import { awaitJSON, MiscLambdaFetch } from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { formatLinkExpiry } from '@src/utils/link-expiry-utils';

type TikTokShopMarket = 'us' | 'row';

interface TikTokShopAuthLink {
  authorizationUrl: string;
  expiresAt: string;
}

const MARKET_OPTIONS = [
  { value: 'us', label: 'US' },
  { value: 'row', label: 'Rest of world' }
];

/**
 * Internal tool for connecting a company's TikTok Shop seller. Generates a signed authorization
 * link to send to the seller. After they approve, TikTok redirects straight to the bpm-lambdas
 * tiktokShopOAuthCallback, which stores the tokens -- so there is no frontend callback page.
 */
export const TikTokShopAuth = () => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();
  const [market, setMarket] = useState<TikTokShopMarket>('us');
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<TikTokShopAuthLink>();
  // Bumped whenever the selection changes, so a response for an older selection is dropped.
  const requestId = useRef(0);

  // The link is only valid for the company and market it was generated for.
  useEffect(() => {
    requestId.current += 1;
    setLink(undefined);
    setLoading(false);
  }, [company, market]);

  const generateLink = useCallback(async () => {
    const id = requestId.current;
    setLoading(true);
    setLink(undefined);
    try {
      const res = await MiscLambdaFetch('/beginTikTokShopAuthorization', {
        params: { company, market }
      });
      const result = await awaitJSON<TikTokShopAuthLink>(res);
      if (id === requestId.current) {
        setLink(result);
      }
    } catch (e) {
      if (id === requestId.current) {
        setError({
          message: `Failed to generate TikTok Shop authorization link: ${e.message}`,
          reportError: e
        });
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  }, [company, market, setError]);

  return (
    <Page pageType='TikTok Shop Auth' title='TikTok Shop Auth' minHeight='400px'>
      <div className='tiktokshop-auth-page'>
        <div className='tiktokshop-auth-card'>
          <p className='tiktokshop-auth-help'>
            Generate an authorization link for <strong>{company}</strong> and send it to the TikTok
            Shop seller. They approve it while logged in to their seller account and see a
            confirmation page; no BPM login is needed.
          </p>
          <div className='tiktokshop-auth-market'>
            <Dropdown
              size={Size.LG}
              label='Market'
              menuPortalTarget={document.body}
              clearable={false}
              value={market}
              options={MARKET_OPTIONS}
              onChange={value => {
                if (value) {
                  setMarket(value as TikTokShopMarket);
                }
              }}
            />
          </div>
          <div>
            <Button size={Size.LG} onClick={generateLink} loading={loading}>
              Generate link
            </Button>
          </div>
          {link && (
            <>
              <div className='tiktokshop-auth-link-row'>
                <Form.Control
                  aria-label='Authorization link'
                  readOnly
                  value={link.authorizationUrl}
                />
                <CopyButton label='Copy' text={link.authorizationUrl} />
              </div>
              <div className='tiktokshop-auth-link-meta'>{formatLinkExpiry(link.expiresAt)}.</div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
};
